import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TRABADJU_INFO as T } from '@/utils/businessInfo';
import { ArrowLeft, Mail, MapPin, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export default function Contacto() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(form.subject || t('contacto.defaultSubject', { name: form.name || t('contacto.defaultVisitor') }));
    const body = encodeURIComponent(`${t('contacto.nameLabel')}: ${form.name}\n${t('contacto.emailLabel')}: ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:${T.email_public}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10" data-testid="page-contacto">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/"><Button variant="ghost" className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" />{t('contacto.back')}</Button></Link>

        {/* Aviso candidatura */}
        <div className="mb-5 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4" role="note" data-testid="contacto-cv-warning">
          <svg className="h-5 w-5 text-amber-600 flex-none mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>
          <p className="text-sm text-amber-900 leading-[1.6]">
            <strong className="font-semibold">{t('contacto.cvWarningLabel')}</strong>{' '}
            {t('contacto.cvWarningText')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <h1 className="text-4xl font-bold mb-2">{t('contacto.title')}</h1>
          <p className="text-gray-600 mb-8">{t('contacto.subtitle')}</p>

          <div className="grid md:grid-cols-2 gap-10">
            {/* Info */}
            <div className="space-y-5">
              <div>
                <h2 className="font-semibold text-lg mb-2">{t('contacto.companyDataTitle')}</h2>
                <p className="text-gray-700 text-sm">{T.legal_name}</p>
                <p className="text-gray-500 text-sm">NIF {T.nif}</p>
              </div>

              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium">{t('contacto.emailLabel')}</p>
                  <a href={`mailto:${T.email_public}`} className="text-blue-600 hover:underline text-sm">{T.email_public}</a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium">{t('contacto.addressLabel')}</p>
                  <p className="text-gray-600 text-sm">{T.address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Instagram className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium">{t('contacto.instagramLabel')}</p>
                  <a
                    href="https://www.instagram.com/trabadju.cv"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >@trabadju.cv</a>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="contact-name">{t('contacto.nameLabel')}</Label>
                <Input id="contact-name" data-testid="contact-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-email">{t('contacto.emailLabel')}</Label>
                <Input id="contact-email" type="email" data-testid="contact-email-input" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-subject">{t('contacto.subjectLabel')}</Label>
                <Input id="contact-subject" data-testid="contact-subject-input" value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-message">{t('contacto.messageLabel')}</Label>
                <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="contact-submit-btn">
                {t('contacto.sendBtn')}
              </Button>
              <p className="text-xs text-gray-500 text-center">{t('contacto.mailHint')}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
